import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, TrendingUp } from 'lucide-react';
import { GlassCard } from '../ui';
import { getUtmBreakdown, UtmBreakdownResponse } from '../../services/api';

// B3 — first-party UTM attribution breakdown. Reference component: the
// Overview tab's "Recent Campaigns" panel (AnalyticsModule.tsx) — same
// GlassCard + .analytics-section-title + .analytics-table structure. Fetches
// its own data so a breakdown failure can never blank the Traffic KPIs.

const GROUPS = [
    { id: 'source', label: 'Source' },
    { id: 'medium', label: 'Medium' },
    { id: 'campaign', label: 'Campaign' },
] as const;

type GroupBy = typeof GROUPS[number]['id'];

const UtmBreakdownPanel: React.FC<{ projectId?: string; days: number }> = ({ projectId, days }) => {
    const [groupBy, setGroupBy] = useState<GroupBy>('source');
    const [data, setData] = useState<UtmBreakdownResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        setError(null);
        try {
            // The endpoint clamps at 90; TrafficTab's day options are within range.
            setData(await getUtmBreakdown(projectId, Math.min(days, 90), groupBy));
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to load UTM breakdown.');
        } finally {
            setLoading(false);
        }
    }, [projectId, days, groupBy]);

    useEffect(() => { load(); }, [load]);

    if (!projectId) return null;

    const groupLabel = GROUPS.find(g => g.id === groupBy)?.label || 'Source';

    return (
        <GlassCard variant="default" padding="md">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                <h3 className="analytics-section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TrendingUp size={16} /> UTM Breakdown
                </h3>
                {/* Pill group — matches TrafficTab's DAY_OPTIONS inline-styled group */}
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {GROUPS.map(g => (
                        <button
                            key={g.id}
                            onClick={() => setGroupBy(g.id)}
                            style={{
                                padding: '4px 12px',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--stroke)',
                                background: groupBy === g.id ? 'var(--primary-500)' : 'var(--panel-strong)',
                                color: groupBy === g.id ? '#fff' : 'var(--text-secondary)',
                                fontSize: 'var(--text-sm)',
                                fontWeight: groupBy === g.id ? 600 : 500,
                                cursor: 'pointer',
                            }}
                        >
                            {g.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading && (
                <div className="analytics-loading"><Loader2 size={16} className="spin" /> Loading…</div>
            )}
            {error && !loading && (
                <div className="analytics-empty" style={{ color: 'var(--danger)' }}>{error}</div>
            )}
            {data && !loading && !error && (
                data.rows.length === 0 ? (
                    <div className="analytics-empty">
                        No tagged traffic in the last {data.date_range.days} days. Build tagged links in the
                        UTM Builder — or set a thank-you destination on a Meta lead form — and they'll show here.
                    </div>
                ) : (
                    <table className="analytics-table" style={{ marginTop: 'var(--space-3)' }}>
                        <thead>
                            <tr>
                                <th>{groupLabel}</th>
                                <th>Sessions</th>
                                <th>Page views</th>
                                <th>Leads</th>
                                <th>Conv. %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map(row => (
                                <tr key={row.key}>
                                    <td>{row.key}</td>
                                    <td>{row.sessions.toLocaleString()}</td>
                                    <td>{row.page_views.toLocaleString()}</td>
                                    <td>{row.leads.toLocaleString()}</td>
                                    <td>{row.conversion_rate.toFixed(1)}%</td>
                                </tr>
                            ))}
                            <tr>
                                <td style={{ color: 'var(--text-secondary)' }}>Untagged</td>
                                <td style={{ color: 'var(--text-secondary)' }}>{data.untagged.sessions.toLocaleString()}</td>
                                <td style={{ color: 'var(--text-secondary)' }}>{data.untagged.page_views.toLocaleString()}</td>
                                <td style={{ color: 'var(--text-secondary)' }}>{data.untagged.leads.toLocaleString()}</td>
                                <td style={{ color: 'var(--text-secondary)' }}>—</td>
                            </tr>
                        </tbody>
                    </table>
                )
            )}
        </GlassCard>
    );
};

export default UtmBreakdownPanel;
